import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import {
  exportProject,
  getExportProgress,
  getProjectExportProgress,
} from '@src/apis/project';
import { downloadFileByUrl, getDownloadFileName } from '@src/utils/file';
import {
  EXPORT_HISTORY_TYPES,
  EXPORT_POLL_FAILURE_BUDGET,
  EXPORT_STATUS,
  FETCH_EXPORT_STATUS_INTERVAL,
} from '@src/constants/project';
import useTrackProjectExported from '@src/hooks/useTrackProjectExported';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';

// Initiate/poll/download cycle for the backend async export pipeline,
// scoped to a single export type. `onSuccess` (optional) runs when the
// export completes, before the file download starts.
const useExportAudio = ({ projectId, exportType, onSuccess }) => {
  const dispatch = useDispatch();
  const exportEntry = useSelector(
    (state) => state.project.exports?.[exportType],
  );
  const trackProjectExport = useTrackProjectExported();
  const exportHistoryEnabled = useExportHistoryEnabled();
  const [isInitiating, setIsInitiating] = useState(false);
  const intervalRef = useRef(null);
  // Id of the export record being polled. Plain values, not refs: both are
  // primitives, so they can sit in the poll loop's dependencies — and the loop
  // only starts once the record is PENDING in redux, which is the same dispatch
  // that stores the id.
  const exportId = exportEntry?.requestId;
  // Only the types the backend keeps in `project_exports` have a record to ask
  // about. SEPARATE_AUDIO_PARTIAL — the block-toolbar export — is not one of
  // them, so it stays on the per-type endpoint whatever the flag says; asking
  // for a record it has no id for answers "Project export is not found", which
  // carries no status and would leave this loop running for ever.
  const hasExportRecord = EXPORT_HISTORY_TYPES.includes(exportType);
  // Tracks the blockIds tied to the in-flight/resumed export so the SUCCESS/
  // FAILURE dispatch doesn't drop them (SET_EXPORT_BY_TYPE replaces the whole
  // export entry rather than merging it).
  const blockIdsRef = useRef(exportEntry?.blockIds);
  // Consecutive ticks that could not answer. A ref, not state: the loop reads
  // and writes it between renders, and a re-render here would re-create the
  // interval.
  const failureCountRef = useRef(0);
  // Ref so a changing callback identity doesn't re-create the poll loop.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  // What a failed export is called. Behind one download button the export and
  // the download are a single action, so a failure there reads as a failed
  // download; the export panel splits them, and the file it exports now is
  // downloaded from the history tab later.
  const exportFailureMessage =
    exportHistoryEnabled && hasExportRecord ? 'exportError' : 'downloadError';

  const isPending = exportEntry?.status === EXPORT_STATUS.PENDING;
  const isExporting = isPending || isInitiating;
  // Who asks the backend about this type. The project-wide poll owns every type
  // the backend keeps a record for, once the record id is known; this hook keeps
  // its own loop for the rest: the block-toolbar export, which has no record,
  // the flag-off flow, and an export whose stored id is not a record id.
  const pollsHere = !(exportHistoryEnabled && hasExportRecord && exportId);
  // Last status seen by this hook. A record that is already SUCCESS when the
  // project opens is not a download that just finished.
  const prevStatusRef = useRef(exportEntry?.status);

  const stopPolling = useCallback(
    (hasError = false) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (hasError) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: exportFailureMessage,
          }),
        );
      }
    },
    [dispatch, exportFailureMessage],
  );

  // A tick that cannot answer is not evidence the export failed: the record can
  // be missing for a moment, and a rejected envelope arrives as HTTP 200. Only a
  // spent budget ends the export, and it writes the final status, so no export
  // is left PENDING with nothing polling it.
  const failTick = useCallback(() => {
    failureCountRef.current += 1;
    if (failureCountRef.current < EXPORT_POLL_FAILURE_BUDGET) return;

    stopPolling();
    dispatch(
      actions.project.setExportByType(exportType, {
        requestId: exportId,
        status: EXPORT_STATUS.FAILURE,
        ...(blockIdsRef.current ? { blockIds: blockIdsRef.current } : {}),
      }),
    );
    dispatch(
      actions.noti.push({ severity: 'error', message: exportFailureMessage }),
    );
  }, [dispatch, exportFailureMessage, exportId, exportType, stopPolling]);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(async () => {
      try {
        // With the record id known, ask about that exact record; the per-type
        // endpoint is the fallback for a type with no record and for an export
        // started before the flow was switched on, whose stored id may not be a
        // record id.
        const useRecordProgress =
          exportHistoryEnabled && hasExportRecord && exportId;
        const response = useRecordProgress
          ? await getProjectExportProgress(projectId, exportId)
          : await getExportProgress(projectId, exportType);
        // A rejected envelope (`status: 0`) arrives as HTTP 200 and carries no
        // status, so it cannot be read as progress — a missing record answers
        // that way.
        if (!response?.status) {
          failTick();
          return;
        }
        failureCountRef.current = 0;

        const progress = useRecordProgress
          ? response?.result
          : response?.result?.progress;
        const { status, fileUrl, fileName } = progress || {};
        // The record endpoint answers with its own id; the per-type one calls
        // the same value `requestId`.
        const requestId = progress?.id || progress?.requestId;
        // Prefer the server-stored blockIds (the per-type progress returns the
        // whole export record); the ref covers both the
        // initiate-before-first-poll window and the record endpoint, which
        // reports no blockIds.
        const blockIds = progress?.blockIds || blockIdsRef.current;
        const exportData = {
          status,
          fileUrl,
          requestId,
          ...(blockIds ? { blockIds } : {}),
        };

        if (status === EXPORT_STATUS.SUCCESS) {
          stopPolling();
          dispatch(actions.project.setExportByType(exportType, exportData));
          trackProjectExport({
            exportType,
            requestId,
          });
          onSuccessRef.current?.();
          // S3 files are cross-origin, so the <a download> attribute is ignored.
          // Fetch as a blob to force a same-origin download. The name comes from
          // the export record (BR_5), with the url supplying what it omits.
          if (fileUrl)
            downloadFileByUrl(fileUrl, getDownloadFileName(fileName, fileUrl));
        } else if (status === EXPORT_STATUS.FAILURE) {
          stopPolling(true);
          dispatch(actions.project.setExportByType(exportType, exportData));
        }
      } catch {
        failTick();
      }
    }, FETCH_EXPORT_STATUS_INTERVAL);
  }, [
    dispatch,
    exportType,
    failTick,
    projectId,
    stopPolling,
    trackProjectExport,
    exportHistoryEnabled,
    hasExportRecord,
    exportId,
  ]);

  // Resume polling on mount if an export for this type is already PENDING
  // (e.g. component remounted while the backend job was still running).
  useEffect(() => {
    if (pollsHere && isPending) startPolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPending, pollsHere, startPolling]);

  // Download and `onSuccess` for the types the project-wide poll owns: it writes
  // the status, and the transition into SUCCESS is what a finished export looks
  // like from here.
  useEffect(() => {
    const status = exportEntry?.status;
    const previous = prevStatusRef.current;
    prevStatusRef.current = status;

    if (pollsHere) return;
    if (previous !== EXPORT_STATUS.PENDING) return;
    if (status !== EXPORT_STATUS.SUCCESS) return;

    trackProjectExport({
      exportType,
      requestId: exportEntry?.requestId,
    });
    onSuccessRef.current?.();
    // S3 files are cross-origin, so the <a download> attribute is ignored.
    // Fetch as a blob to force a same-origin download. The name comes from the
    // export record (BR_5), with the url supplying what it omits.
    const { fileUrl, fileName } = exportEntry || {};
    if (fileUrl)
      downloadFileByUrl(fileUrl, getDownloadFileName(fileName, fileUrl));
  }, [exportEntry, exportType, pollsHere, trackProjectExport]);

  const startExport = useCallback(
    async (blockIds) => {
      setIsInitiating(true);
      blockIdsRef.current = blockIds;
      failureCountRef.current = 0;
      try {
        const response = await exportProject(
          projectId,
          exportType,
          undefined,
          blockIds,
        );
        if (!response?.status) {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: exportFailureMessage,
            }),
          );
          return;
        }
        const requestId = response?.result?.requestId || response?.result?.id;
        dispatch(
          actions.project.setExportByType(exportType, {
            requestId,
            status: EXPORT_STATUS.PENDING,
            ...(blockIds ? { blockIds } : {}),
          }),
        );
      } catch {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: exportFailureMessage,
          }),
        );
      } finally {
        setIsInitiating(false);
      }
    },
    [dispatch, exportFailureMessage, projectId, exportType],
  );

  return { isExporting, startExport };
};

export default useExportAudio;
